import { Injectable } from '@angular/core';

interface CoreUsage {
  coreId: number;
  usagePercent: number;
  cpuUsed: {
    user: number;
    nice: number;
    system: number;
    idle: number;
    iow: number;
    irq: number;
    sirq: number;
    host: number;
  };
}

interface CPUUsage {
  coreId: number;
  userPercent: number;
  nicePercent: number;
  sysPercent: number;
  idlePercent: number;
  iowPercent: number;
  irqPercent: number;
  sirqPercent: number;
  hostPercent: number;
}

interface ProcessInfo {
  pid: string;
  name: string;
  cpuPercent: number;
  cpuCores: number;
  memoryUsed: string;
  coreAffinity?: number[];
  threadCount?: number;
}

interface SystemResources {
  processes: ProcessInfo[];
  totalMemory: string;
  usedMemory: string;
  freeMemory: string;
  memoryUsagePercent: number;
  totalCpuCores: number;
  coreUsages: CoreUsage[];
  cpuUsage: CPUUsage[];
}

@Injectable({
  providedIn: 'root'
})
export class ProcessMonitorService {
  
  parseSystemData(rawData: string[]): SystemResources {
    let processes: ProcessInfo[] = [];
    let memoryStats = {
      total: '',
      used: '',
      free: ''
    };
    let coreUsages: CoreUsage[] = [];
    let cpuUsage: CPUUsage[] = [];
    let totalCpuCores = 8; // Default to 8 cores
  
    rawData.forEach(snapshot => {
      const lines = snapshot.split('\n');
  
      // Extract memory information
      const memLine = lines.find(line => line.includes('Mem:'));
      if (memLine) {
        const parts = memLine.split(',');
        memoryStats = {
          total: this.formatMemory(parts[0].split(':')[1].trim()),
          used: this.formatMemory(parts[1].trim()),
          free: this.formatMemory(parts[2].trim())
        };
      }
  
      // Extract detailed core usages
      coreUsages = this.extractDetailedCoreUsages(lines);
      cpuUsage = this.extractCPUUsageDetails(lines);
      totalCpuCores = coreUsages.length;
  
      // Extract process information
      processes = this.extractProcesses(lines, coreUsages);
    });
  
    // Remove duplicates and sort by CPU usage
    processes = Array.from(new Map(processes.map(p => [p.pid, p])).values())
      .sort((a, b) => b.cpuPercent - a.cpuPercent);
  
    const totalMem = parseFloat(memoryStats.total.replace('MB', ''));
    const usedMem = parseFloat(memoryStats.used.replace('MB', ''));
    const memoryUsagePercent = (usedMem / totalMem) * 100;
  
    return {
      processes,
      totalMemory: memoryStats.total,
      usedMemory: memoryStats.used,
      freeMemory: memoryStats.free,
      memoryUsagePercent,
      totalCpuCores,
      coreUsages,
      cpuUsage
    };
  }
  
  private extractDetailedCoreUsages(lines: string[]): CoreUsage[] {
    const coreUsages: CoreUsage[] = [];
    
    // Pattern to match the specific core usage format
    const corePattern = /(\d+)%user\s+(\d+)%nice\s+(\d+)%sys\s+(\d+)%idle\s+(\d+)%iow\s+(\d+)%irq\s+(\d+)%sirq\s+(\d+)%host/;
    
    // Find the line with core usage details
    const usageLine = lines.find(line => corePattern.test(line));
    
    if (usageLine) {
      const match = usageLine.match(corePattern);
      
      if (match) {
        const [
          , userPercent, 
          nicePercent, 
          sysPercent, 
          idlePercent, 
          iowPercent, 
          irqPercent, 
          sirqPercent, 
          hostPercent
        ] = match;
        
        // Create a single core usage object representing the entire CPU
        const coreUsage: CoreUsage = {
          coreId: 1, // Represents the entire CPU
          usagePercent: Math.min(
            parseFloat(userPercent) + 
            parseFloat(nicePercent) + 
            parseFloat(sysPercent), 
            100
          ),
          cpuUsed: {
            user: parseFloat(userPercent),
            nice: parseFloat(nicePercent),
            system: parseFloat(sysPercent),
            idle: parseFloat(idlePercent),
            iow: parseFloat(iowPercent),
            irq: parseFloat(irqPercent),
            sirq: parseFloat(sirqPercent),
            host: parseFloat(hostPercent)
          }
        };
        
        coreUsages.push(coreUsage);
      }
    }

    return coreUsages;
  }
  
  private extractCPUUsageDetails(lines: string[]): CPUUsage[] {
    const cpuUsage: CPUUsage[] = [];
    
    // Pattern to match the specific core usage format
    const corePattern = /(\d+)%user\s+(\d+)%nice\s+(\d+)%sys\s+(\d+)%idle\s+(\d+)%iow\s+(\d+)%irq\s+(\d+)%sirq\s+(\d+)%host/;
    
    // Find the line with core usage details
    const usageLine = lines.find(line => corePattern.test(line));
    
    if (usageLine) {
      const match = usageLine.match(corePattern);
      
      if (match) {
        const [
          , userPercent, 
          nicePercent, 
          sysPercent, 
          idlePercent, 
          iowPercent, 
          irqPercent, 
          sirqPercent, 
          hostPercent
        ] = match;
        
        const coreCPUUsage: CPUUsage = {
          coreId: 1, // Represents the entire CPU
          userPercent: parseFloat(userPercent),
          nicePercent: parseFloat(nicePercent),
          sysPercent: parseFloat(sysPercent),
          idlePercent: parseFloat(idlePercent),
          iowPercent: parseFloat(iowPercent),
          irqPercent: parseFloat(irqPercent),
          sirqPercent: parseFloat(sirqPercent),
          hostPercent: parseFloat(hostPercent)
        };
        
        cpuUsage.push(coreCPUUsage);
      }
    }

    return cpuUsage;
  }
  
  private extractProcesses(lines: string[], coreUsages: CoreUsage[]): ProcessInfo[] {
    const processes: ProcessInfo[] = [];
    const totalCpuCores = coreUsages.length;
    
    lines.forEach(line => {
      // Skip kernel workers and ensure line starts with a process ID
      if (line && !line.includes('[kworker') && /^\s*\d+/.test(line)) {
        const processInfo = this.parseProcessLine(line, totalCpuCores);
        if (processInfo && processInfo.cpuPercent > 0) {
          processes.push(processInfo);
        }
      }
    });

    return processes;
  }
  
  private parseProcessLine(line: string, totalCpuCores: number): ProcessInfo | null {
    try {
      const parts = line.trim().split(/\s+/);
      if (parts.length < 11) return null;
  
      // Parse CPU percentage and determine number of cores
      const cpuPercent = Math.min(
        parseFloat(parts[8].replace('%', '')), 
        100 * totalCpuCores
      );
      const cpuCores = Math.min(
        Math.ceil(cpuPercent / 100), 
        totalCpuCores
      );
  
      return {
        pid: parts[0],
        name: parts.slice(10).join(' '),
        cpuPercent: cpuPercent,
        cpuCores: cpuCores,
        memoryUsed: this.formatMemory(parts[5]),
        coreAffinity: this.extractCoreAffinity(line),
        threadCount: this.extractThreadCount(line)
      };
    } catch {
      return null;
    }
  }
  
  private extractCoreAffinity(line: string): number[] {
    // Attempt to extract core affinity information
    const coreAffinityMatch = line.match(/P(\d+)/);
    return coreAffinityMatch 
      ? [parseInt(coreAffinityMatch[1], 10)] 
      : [];
  }
  
  private extractThreadCount(line: string): number {
    // Attempt to extract thread count information
    const threadCountMatch = line.match(/\b(\d+)\s*threads\b/);
    return threadCountMatch 
      ? parseInt(threadCountMatch[1], 10) 
      : 1;
  }
  
  private formatMemory(memory: string): string {
    // Convert KB to MB
    if (memory.endsWith('K')) {
      const kb = parseFloat(memory.replace('K', ''));
      return `${(kb / 1024).toFixed(2)}MB`;
    }
    return memory;
  }
}